import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { FaArrowAltCircleLeft, FaArrowAltCircleRight } from 'react-icons/fa';
import styled from 'styled-components';
import { toaster } from 'src/helpers/UtilityHelper';
import StyleConfig from 'src/constants/StyleConfig';
import InputFileButton from 'src/components/atoms/form/InputFileButton';
import InputLabelHolder from 'src/components/molecules/form/InputLabelHolder';

type Props = {
  label: string;
  thumbnails: string[];
  multiple?: boolean;
  onChange: (thumbnails: string[]) => void;
  onClickPrev?: (index: number) => void;
  onClickNext?: (index: number) => void;
  canUploadImageLength?: number;
};

type Image = {
  src: string;
  fileName: string;
};

type Images = Image[];

type PreviewBlockProps = {
  images: Images;
  onDelete: (index: number) => void;
  onClickPrev?: (index: number) => void;
  onClickNext?: (index: number) => void;
  canUploadImageLength?: number;
};

const PreviewBlock = ({ canUploadImageLength, images, onDelete, onClickPrev, onClickNext, }: PreviewBlockProps) => {
  const isSingleView = canUploadImageLength && canUploadImageLength === 1;

  const hasImages = useMemo(() => images && images.length > 1, [images])

  const wrapOnClickPrev = useCallback((e: React.TouchEvent<HTMLButtonElement> | React.MouseEvent<HTMLButtonElement>) => {
    const value = parseInt(e.currentTarget.value, 10);
    if (onClickPrev) {
      onClickPrev(value)
    }
  }, [onClickPrev]);

  const wrapOnClickNext = useCallback((e: React.TouchEvent<HTMLButtonElement> | React.MouseEvent<HTMLButtonElement>) => {
    const value = parseInt(e.currentTarget.value, 10);
    if (onClickNext) {
      onClickNext(value);
    }
  }, [onClickNext]);

  return (
    <ul
      className={`
      preview 
      ${isSingleView ? 'is-singleImage' : ''}
    `}
    >
      {images.map((image, index) => (
        <li key={index}>
          <img src={image.src} alt={image.fileName} />
          <div className="actions">
            {hasImages && onClickPrev && (
              <button type="button" className={`action-prev ${index === 0 ? 'is-hidden' : ''}`} value={index} onClick={wrapOnClickPrev}>
                <FaArrowAltCircleLeft size="28" />
              </button>
            )}
            <span className={'image-delete mt8'} onClick={() => onDelete(index)}>
              削除する
            </span>
            {hasImages && onClickNext && (
              <button type="button" className={`action-next ${index === (images.length - 1) ? 'is-hidden' : ''}`}  value={index} onClick={wrapOnClickNext}>
                <FaArrowAltCircleRight size="28" />
              </button>
            )}
          </div>

        </li>
      ))}
    </ul>
  );
};

const UploadImageBlock = ({ label, thumbnails, multiple, onChange, onClickPrev, onClickNext, canUploadImageLength }: Props) => {
  const [images, setImages] = useState<Images>([]);

  useEffect(() => {
    setImages(
      thumbnails.map((v) => ({
        src: v,
        fileName: '',
      })),
    );
  }, [thumbnails]);

  const handleChange = useCallback((images: Images) => {
    onChange(images.map(({ src }) => src as string));
  }, [onChange]);

  const trimNameExtension = useCallback((name: string) =>
    name.toLowerCase().replace(/.(jpg|jpeg|gif|png|bmp)/, ''), []);

  const readAndLoadFile = useCallback(async (file: File | File[]) => {
    
    // 複数ファイルの場合、readerの非同期処理を制御
    if (Array.isArray(file)) {
      const merge: Images = [];
      (async () => {
          await Promise.all(
            file.map(async v => {
              const reader: FileReader = await readerOnloadend(v);
              merge.push({ fileName: '', src: reader.result as string });
            })
          );
          handleChange([
            ...images,
            ...merge,
          ]);
      })();
    } else {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        handleChange([
          ...images,
          {
            src: reader.result as string,
            fileName: trimNameExtension(file.name),
          },
        ]);
      };
    }
  
  }, [images, handleChange, trimNameExtension]);

  // await reader.onloadend
  const readerOnloadend = async (data: File): Promise<FileReader> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            resolve(reader);
        };
        reader.readAsDataURL(data);
    });
};

  const handleUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files.item(0);
    if (!file) return;

    // FileListにはforしか使えないのでやむなく...
    const errors = [];
    const length = files.length;
    for (let i = 0; i < length; i++) {
      const target = files[i];
      errors.push(target.size > 10485760); // 10MBを超えるか
    }
    if (errors.some((e) => e)) {
      toaster('error', '画像のサイズが10MBを超えています。');
      return;
    }
    if (multiple) {
      // 複数の場合
      const values = [];
      for (let i = 0; i < length; i++) {
        const value = files.item(i);
        if (value) {
          values.push(value);
        }
      }
      if (values.length) {
        readAndLoadFile(values);
      }
    } else {
      readAndLoadFile(file);
    }
  }, [multiple, readAndLoadFile]);

  const handleDelete = (index: number) => {
    handleChange(images.filter((_, i) => i !== index));
  };

  let isVisibleUploadButton = true;
  if (canUploadImageLength && canUploadImageLength <= images.length) {
    isVisibleUploadButton = false;
  }
  return (
    <Styled>
      <InputLabelHolder labelName={label} isRequired={true} />
      {images ? (
        <PreviewBlock
          canUploadImageLength={canUploadImageLength}
          images={images}
          onDelete={handleDelete}
          onClickPrev={onClickPrev}
          onClickNext={onClickNext}
        />
      ) : null}
      <div style={{ display: isVisibleUploadButton ? 'block' : 'none' }}>
        <InputFileButton id={'upload_image'} onChange={handleUpload} multiple={multiple} />
        <p className="info">※jpeg,jpg,pngの10MB以内の画像をアップロードできます</p>
      </div>
    </Styled>
  );
};

const Styled = styled.div`
  .info {
    font-size: ${StyleConfig.TYPO.SMLTEXT};
    color: ${StyleConfig.COLOR.TEXT_GRAY};
    text-align: center;
    margin-top: ${StyleConfig.HELPER.M12};
  }
  .preview {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    li {
      width: 48%;
      justify-content: center;
      align-items: center;
      margin-bottom: ${StyleConfig.HELPER.M24};
      text-align: center;
      align-self: flex-end;

      img {
        width: 100%;
      }

      .actions {
        margin-top: 8px;
        width: 100%;
        justify-content: center;
        align-items: center;
        display: flex;
        flex-wrap: wrap;

        span {
          margin: 0 auto;
        }

        button {
          &.is-hidden {
            visibility: hidden;
          }

          svg {
            color: #2A7589;
          }
        }
      }

    }
    &.is-singleImage {
      li {
        width: 100%;
      }
    }
    .image-delete {
      font-size: ${StyleConfig.TYPO.SMLTEXT};
      color: ${StyleConfig.COLOR.SECONDORY};
    }
  }
  .mt8 {
    margin-top: ${StyleConfig.HELPER.M8};
  }
  > .submit {
    padding: ${StyleConfig.HELPER.M24} 0 ${StyleConfig.HELPER.M48} 0;
    .mt20 {
      margin-top: ${StyleConfig.HELPER.M20};
    }
  }
`;

export default UploadImageBlock;
